'use client';

import React, { useEffect, useRef, useState } from 'react';
import { m } from 'motion/react';
import { css } from 'styled-system/css';

interface PointerArrowProps {
  /** Selector of the note the arrow starts from (inside the same positioned parent) */
  from: string;
  /** Selector of the round thing it points at */
  to: string;
  className?: string;
  /** Seconds before it draws itself in */
  delay?: number;
}

type Geometry = { d: string; head: string; width: number; height: number };

const stroke = css({
  fill: 'none',
  stroke: 'fg.hot',
  strokeWidth: '{borderWidths.doodle}',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

/**
 * A hand-drawn arrow from a note to a circle (the portrait), drawn from where both really are.
 * It's re-measured whenever the layout changes, so it keeps pointing at the portrait at any
 * width, font size or language. Draws itself in once.
 */
export function PointerArrow({ from, to, className, delay = 0.7 }: PointerArrowProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [geometry, setGeometry] = useState<Geometry | null>(null);

  useEffect(() => {
    const svg = svgRef.current;
    const container = svg?.parentElement;
    if (!svg || !container) return;

    const measure = () => {
      const start = container.querySelector(from);
      const end = container.querySelector(to);
      if (!start || !end) return;
      const box = container.getBoundingClientRect();
      const a = start.getBoundingClientRect();
      const b = end.getBoundingClientRect();
      if (!a.width || !b.width) return setGeometry(null);

      // start just below the note's end; aim at the circle's rim, facing the note
      const sx = a.right - box.left - a.width * 0.15;
      const sy = a.bottom - box.top + 6;
      const cx = b.left - box.left + b.width / 2;
      const cy = b.top - box.top + b.height / 2;
      const r = Math.min(b.width, b.height) / 2;
      const angle = Math.atan2(sy - cy, sx - cx) + 0.35; // land a little above the line of sight
      const ex = cx + Math.cos(angle) * (r + 14);
      const ey = cy + Math.sin(angle) * (r + 14);

      const dx = ex - sx;
      const lift = Math.max(40, Math.abs(dx) * 0.25);
      const c1x = sx + dx * 0.35;
      const c1y = sy - lift;
      const c2x = ex - dx * 0.25;
      const c2y = ey - lift * 0.9;
      const d = `M${sx},${sy} C${c1x},${c1y} ${c2x},${c2y} ${ex},${ey}`;

      // arrowhead along the curve's direction at the end
      const t = Math.atan2(ey - c2y, ex - c2x);
      const len = 16;
      const wing = (side: number) =>
        `${ex - len * Math.cos(t + side * 0.5)},${ey - len * Math.sin(t + side * 0.5)}`;
      const head = `M${wing(1)} L${ex},${ey} L${wing(-1)}`;

      setGeometry({ d, head, width: box.width, height: box.height });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    container.querySelectorAll(`${from}, ${to}`).forEach((el) => observer.observe(el));
    document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [from, to]);

  return (
    <svg
      ref={svgRef}
      aria-hidden
      className={className}
      width={geometry?.width ?? 0}
      height={geometry?.height ?? 0}
      style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}
    >
      {geometry && (
        <>
          <m.path
            d={geometry.d}
            className={stroke}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.2, delay, ease: [0.22, 1, 0.36, 1] }}
          />
          <m.path
            d={geometry.head}
            className={stroke}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: delay + 1.05 }}
          />
        </>
      )}
    </svg>
  );
}
